import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { fmtDateTime } from "../../lib/format";
import StatusBadge from "../StatusBadge";
import { Undo2, AlertTriangle } from "lucide-react";

export default function DistributionDetail({ distribution, onClose, onChanged }) {
  const [d, setD] = useState(distribution);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  useEffect(() => { setD(distribution); }, [distribution]);

  const reversed = !!d?.reversed_at;
  const doReverse = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/procurement/distributions/${d.id}/reverse`, { reason });
      toast.success(`Reversed as ${res.number} — stock returned to Central`);
      onChanged(); onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.shortfall) {
        toast.error(detail.message + " Shortfall: " + detail.shortfall.map((s) => `${s.location_name}: ${s.available}/${s.required}`).join(", "));
      } else {
        toast.error(formatApiError(e));
      }
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="dist-detail">
        <DialogHeader>
          <DialogTitle className="font-brand flex items-center gap-3" style={{ color: "#8B1E1E" }}>
            {d.number} <StatusBadge status={reversed ? "reversed" : "active"} />
          </DialogTitle>
          <DialogDescription>{d.product_name} · {fmtDateTime(d.created_at)} · by {d.created_by_name}</DialogDescription>
        </DialogHeader>

        <div className="rounded-2xl border" style={{ borderColor: "#E9DED0" }}>
          <div className="grid grid-cols-[1fr_100px_120px] text-[11px] uppercase tracking-widest font-semibold px-3 py-2 border-b" style={{ color: "#765A4A", borderColor: "#E9DED0" }}>
            <div>Branch</div><div className="text-right">Qty</div><div className="text-right">Unit cost</div>
          </div>
          {d.allocations.map((a, i) => (
            <div key={i} className="grid grid-cols-[1fr_100px_120px] px-3 py-2 text-sm" style={{ color: "#3B2722" }}>
              <div>{a.location_name}</div>
              <div className="text-right font-semibold">{a.quantity}</div>
              <div className="text-right">{Number(a.unit_cost || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
            </div>
          ))}
          <div className="grid grid-cols-[1fr_100px_120px] px-3 py-2 text-sm border-t font-semibold" style={{ background: "#FFF9EE", borderColor: "#E9DED0" }}>
            <div>Total</div>
            <div className="text-right" style={{ color: "#8B1E1E" }}>{d.total_quantity}</div>
            <div />
          </div>
        </div>

        {d.note && <div className="text-sm mt-3 p-3 rounded-xl" style={{ background: "#FFF9EE", color: "#3B2722" }}>{d.note}</div>}

        {reversed ? (
          <div className="mt-3 p-3 rounded-xl border" style={{ background: "#F2E9DF", borderColor: "#E9DED0", color: "#3B2722" }}>
            <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "#765A4A" }}>Reversed</div>
            <div className="text-sm mt-1">{d.reversal_number} · {fmtDateTime(d.reversed_at)} · by {d.reversed_by_name}</div>
            {d.reversal_reason && <div className="text-xs mt-1" style={{ color: "#a58f7c" }}>Reason: {d.reversal_reason}</div>}
          </div>
        ) : confirm ? (
          <div className="mt-3 p-3 rounded-xl border" style={{ background: "#FBEBEB", borderColor: "#F0C0C0", color: "#3B2722" }} data-testid="dist-reverse-confirm">
            <div className="flex items-start gap-2"><AlertTriangle size={16} style={{ color: "#A52222", marginTop: 2 }} /><div>
              <div className="text-sm font-semibold">Reverse this distribution?</div>
              <div className="text-xs mt-1" style={{ color: "#765A4A" }}>Stock at each branch will be consumed and returned to Central. Blocked if a branch has already used any of it.</div>
              <Label className="mt-2 block">Reason (optional)</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="dist-reverse-reason" placeholder="e.g. sent to wrong branch" />
            </div></div>
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="dist-detail-close">Close</Button>
          {!reversed && (
            confirm ? (
              <Button onClick={doReverse} disabled={busy} className="text-white" style={{ background: "#A52222" }} data-testid="dist-reverse-confirm-btn">
                <Undo2 size={15} className="mr-1" /> {busy ? "Reversing…" : "Confirm Reversal"}
              </Button>
            ) : (
              <Button onClick={() => setConfirm(true)} variant="outline" data-testid="dist-reverse-btn">
                <Undo2 size={15} className="mr-1" /> Reverse
              </Button>
            )
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
